import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Plus, Trash2, Save, X, Vote, ThumbsUp, ThumbsDown, MinusCircle } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, Card, PrimaryButton, SecondaryButton, Input, Textarea, EmptyState, Badge } from "./_ui";

const EMPTY = { title: "", description: "", closes_at: "" };

const CHOICE_BTN = {
  pour: { Icon: ThumbsUp, label: "Pour", color: "#34D399", bg: "bg-emerald-500/15 hover:bg-emerald-500/25 border-emerald-500/40 text-emerald-300" },
  contre: { Icon: ThumbsDown, label: "Contre", color: "#F87171", bg: "bg-red-500/15 hover:bg-red-500/25 border-red-500/40 text-red-300" },
  abstention: { Icon: MinusCircle, label: "Abstention", color: "#94A3B8", bg: "bg-white/5 hover:bg-white/10 border-white/15 text-slate-300" },
};

export default function VotesPanel() {
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [voting, setVoting] = useState(null);

  const load = () => api.get("/admin/resolutions").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title || !form.description || !form.closes_at) return toast.error("Tous les champs sont requis");
    setBusy(true);
    try {
      await api.post("/admin/resolutions", { ...form, closes_at: new Date(form.closes_at).toISOString() });
      toast.success("Résolution publiée");
      setAdding(false); setForm(EMPTY); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const cast = async (rid, choice) => {
    setVoting(rid + choice);
    try {
      await api.post(`/admin/resolutions/${rid}/vote`, { choice });
      toast.success(`Vote enregistré : ${choice}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setVoting(null); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette résolution ?")) return;
    await api.delete(`/admin/resolutions/${id}`);
    toast.success("Supprimée"); await load();
  };

  if (!items) return (<><PageHeader title="Votes" /><Spinner /></>);

  return (
    <div data-testid="admin-votes">
      <PageHeader
        title="Votes"
        subtitle="Résolutions du Bureau Politique — vote nominatif Pour / Contre / Abstention."
        action={
          !adding && (
            <PrimaryButton onClick={() => setAdding(true)} data-testid="res-create-btn">
              <Plus size={16} /> Nouvelle résolution
            </PrimaryButton>
          )
        }
      />

      {adding && (
        <Card className="p-5 mb-6">
          <Input placeholder="Intitulé de la résolution" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="res-title" />
          <Textarea placeholder="Texte de la résolution soumise au vote…" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-3" data-testid="res-desc" />
          <div className="mt-3">
            <label className="text-xs text-slate-400 mb-1 block">Clôture du scrutin</label>
            <Input type="datetime-local" value={form.closes_at} onChange={(e) => setForm({ ...form, closes_at: e.target.value })} data-testid="res-close" />
          </div>
          <div className="flex gap-3 mt-4">
            <PrimaryButton onClick={save} disabled={busy} data-testid="res-save"><Save size={16} /> Publier</PrimaryButton>
            <SecondaryButton onClick={() => { setAdding(false); setForm(EMPTY); }}><X size={16} /> Annuler</SecondaryButton>
          </div>
        </Card>
      )}

      {items.length === 0 ? (
        <EmptyState icon={Vote} title="Aucune résolution" hint="Soumettez la première résolution au vote du bureau." />
      ) : (
        <div className="space-y-4">
          {items.map((r) => {
            const open = new Date(r.closes_at) > new Date();
            const total = r.votes_count || 1;
            return (
              <Card key={r.id} className="p-5" data-testid={`res-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Vote size={15} className="text-[#D4AF37]" />
                      <h3 className="font-display text-lg text-white">{r.title}</h3>
                      <Badge color={open ? "green" : "slate"}>{open ? "Ouvert" : "Clôturé"}</Badge>
                    </div>
                    <p className="text-sm text-slate-300 mt-2 whitespace-pre-line">{r.description}</p>
                    <div className="text-xs text-slate-500 mt-2">
                      Clôture {new Date(r.closes_at).toLocaleString("fr-FR")} · {r.votes_count} vote(s) exprimé(s)
                    </div>
                  </div>
                  <button onClick={() => remove(r.id)} className="text-slate-500 hover:text-[#CE1126]"><Trash2 size={14} /></button>
                </div>

                <div className="mt-5 grid grid-cols-3 gap-2">
                  {Object.entries(CHOICE_BTN).map(([key, meta]) => {
                    const count = r.tally?.[key] || 0;
                    const pct = r.votes_count ? Math.round((count / total) * 100) : 0;
                    return (
                      <div key={key}>
                        <button
                          onClick={() => open && cast(r.id, key)}
                          disabled={!open || voting === r.id + key}
                          className={`w-full px-3 py-2.5 rounded-lg text-xs border inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${meta.bg}`}
                          data-testid={`res-vote-${r.id}-${key}`}
                        >
                          <meta.Icon size={13} /> {meta.label} ({count})
                        </button>
                        <div className="h-1 mt-2 rounded-full bg-white/5">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: meta.color }} />
                        </div>
                        <div className="text-[10px] text-slate-500 mt-1 text-center">{pct}%</div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
